// The bridge stores the icon inline and caps it at 50 KB, so any picked image is resized and
// re-encoded here instead of asking the user to shrink it.
export const MAX_ICON_BYTES = 50 * 1024
export const MAX_SOURCE_BYTES = 5 * 1024 * 1024
export const ACCEPTED_ICON_TYPES = [
  'image/png',
  'image/jpeg',
  'image/webp',
  'image/gif',
  'image/bmp',
  'image/svg+xml',
]
const MAX_SIDE = 256
const LOSSY_QUALITIES = [0.9, 0.75, 0.6, 0.45]

export const fitWithin = (width: number, height: number, max = MAX_SIDE) => {
  const scale = Math.min(1, max / Math.max(width, height))
  return {
    width: Math.max(1, Math.round(width * scale)),
    height: Math.max(1, Math.round(height * scale)),
  }
}

export const dataUriBytes = (uri: string) => {
  const base64 = uri.slice(uri.indexOf(',') + 1)
  const padding = base64.endsWith('==') ? 2 : base64.endsWith('=') ? 1 : 0
  return (base64.length * 3) / 4 - padding
}

const loadImage = (file: File) =>
  new Promise<HTMLImageElement>((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const image = new Image()
    image.onload = () => {
      URL.revokeObjectURL(url)
      resolve(image)
    }
    image.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('unreadable'))
    }
    image.src = url
  })

const draw = (image: HTMLImageElement, width: number, height: number, background?: string) => {
  const canvas = document.createElement('canvas')
  canvas.width = width
  canvas.height = height
  const context = canvas.getContext('2d')
  if (!context) throw new Error('unreadable')
  if (background) {
    context.fillStyle = background
    context.fillRect(0, 0, width, height)
  }
  context.drawImage(image, 0, 0, width, height)
  return canvas
}

/** Resolves to a PNG, WebP or JPEG data URI of at most `MAX_ICON_BYTES`, smallest-loss first. */
export const encodeIcon = async (file: File): Promise<string> => {
  const image = await loadImage(file)
  // An SVG without width/height reports 0; draw it at the target size.
  const { width, height } = fitWithin(
    image.naturalWidth || MAX_SIDE,
    image.naturalHeight || MAX_SIDE,
  )
  const canvas = draw(image, width, height)
  const fits = (uri: string, type: string) =>
    uri.startsWith(`data:${type}`) && dataUriBytes(uri) <= MAX_ICON_BYTES

  const png = canvas.toDataURL('image/png')
  if (fits(png, 'image/png')) return png
  for (const quality of LOSSY_QUALITIES) {
    const webp = canvas.toDataURL('image/webp', quality)
    if (fits(webp, 'image/webp')) return webp
  }
  // JPEG has no alpha channel, so transparent areas would turn black without a backdrop.
  const flat = draw(image, width, height, '#ffffff')
  for (const quality of LOSSY_QUALITIES) {
    const jpeg = flat.toDataURL('image/jpeg', quality)
    if (fits(jpeg, 'image/jpeg')) return jpeg
  }
  throw new Error('too-large')
}
